import qs from 'qs';
import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_INVOICEITEMS_ORDERING = 'position';
const LEGACY_INVOICEITEMS_ORDERING = 'billing_date,id';

function normalizeInvoiceItemsOrdering(ordering) {
    if (!ordering || ordering === LEGACY_INVOICEITEMS_ORDERING) {
        return DEFAULT_INVOICEITEMS_ORDERING;
    }
    return ordering;
}

const DEFAULT_INVOICEITEMS_FILTERS = { offset: 0, limit: 20, ordering: DEFAULT_INVOICEITEMS_ORDERING };

const state = {
    invoice: null,
    invoice_loading: false,
    invoice_loading_errors: null,

    marking_as_sent: false,
    marking_as_paid: false,
    invalidating_invoice: false,

    invoiceitems: [],
    invoiceitems_count: 0,
    invoiceitems_filters: DEFAULT_INVOICEITEMS_FILTERS,
    invoiceitems_loading: false,
    invoiceitems_loading_errors: null,
    invoiceitems_loaded: false,
    invoiceitems_cancel_source: null,

    invoice_saving: false,
    invoice_saving_errors: {},

    entities: [],
    entities_loading: false,
    entities_loading_errors: null,

    billingaccount: null,
    billingaccount_loading: false,
    billingaccount_loading_errors: null,

    events: [],
    events_filters: { offset: 0, limit: 20 },
    events_count: 0,
    events_loading: false,
    events_errors: null,

    selected_items: [],
    bulk_action_loading: false,
};

const mutations = {

    updateInvoice(state, invoice) {
        if (invoice && state.invoice && state.invoice.id == invoice.id) {
            state.invoice = invoice;
            return;
        }

        state.invoice = invoice;
        state.invoiceitems = [];
        state.invoiceitems_count = 0;
        state.invoiceitems_loaded = false;
        state.invoice_saving_errors = {};

        if (!invoice) {
            state.invoiceitems_filters = { offset: 0, limit: state.invoiceitems_filters.limit, ordering: DEFAULT_INVOICEITEMS_ORDERING };
            state.events_filters = { offset: 0, limit: state.events_filters.limit };
            return;
        }
        const persistedItemsFilters = loadFiltersFromStorage(getStorageKey('customerinvoice', 'items'), { limit: 20, ordering: DEFAULT_INVOICEITEMS_ORDERING });
        state.invoiceitems_filters = {
            invoice: invoice.id,
            offset: 0,
            limit: persistedItemsFilters.limit,
            ordering: normalizeInvoiceItemsOrdering(persistedItemsFilters.ordering),
        }

        state.events_filters = {
            offset: 0,
            limit: state.events_filters.limit,
            customerinvoice: invoice.id,
            object_type: 'customerinvoice', object_id: state.invoice.id,
            ordering: '-event_date',
        }
    },
    updateInvoiceLoading(state, loading) {
        state.invoice_loading = loading;
    },
    updateInvoiceLoadingErrors(state, errors) {
        state.invoice_loading_errors = errors;
    },

    updateInvoiceItemSaving(state, invoice_saving) {
        state.invoice_saving = invoice_saving;
    },
    updateInvoiceItemSavingErrors(state, invoice_saving_errors) {
        state.invoice_saving_errors = invoice_saving_errors;
        state.invoice_saving = false;
    },

    updateInvoiceItems(state, invoiceitems) {
        state.invoiceitems = invoiceitems;
    },
    updateInvoiceItemsLoading(state, loading) {
        state.invoiceitems_loading = loading;
    },
    updateInvoiceItemsCount(state, count) {
        state.invoiceitems_count = count;
    },
    updateInvoiceItemsLoadingErrors(state, errors) {
        state.invoiceitems_loading_errors = errors;
    },
    updateInvoiceItemsFilters(state, filters) {
        state.invoiceitems_filters = filters;
        saveFiltersToStorage(getStorageKey('customerinvoice', 'items'), filters, ['limit', 'ordering']);
    },
    updateInvoiceItemsLoaded(state, loaded) {
        state.invoiceitems_loaded = loaded;
    },
    updateInvoiceItemsCancelSource(state, cancelSource) {
        state.invoiceitems_cancel_source = cancelSource;
    },
    updateSelectedItems(state, items) {
        state.selected_items = items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },


    updateEntities(state, entities) {
        state.entities = entities;
        state.entities_loading = false;
        state.entities_loading_errors = null;
    },
    updateEntitiesLoading(state, loading) {
        state.entities_loading = loading;
        state.entities_loading_errors = null;
    },
    updateEntitiesLoadingErrors(state, errors) {
        state.entities_loading = false;
        state.entities_loading_errors = errors;
    },

    updateBillingAccount(state, billingaccount) {
        state.billingaccount = billingaccount;
        state.billingaccount_loading = false;
        state.billingaccount_loading_errors = null;
    },
    updateBillingAccountLoading(state, billingaccount_loading) {
        state.billingaccount_loading = billingaccount_loading;
    },
    updateBillingAccountLoadingErrors(state, billingaccount_loading_errors) {
        state.billingaccount_loading_errors = billingaccount_loading_errors;
    },

    updateMarkingAsPaid(state, marking) {
        state.marking_as_paid = marking;
    },
    updateInvalidatingInvoice(state, invalidating) {
        state.invalidating_invoice = invalidating;
    },

    updateMarkingAsSent(state, marking_as_sent) {
        state.marking_as_sent = marking_as_sent;
    },

    updateEvents(state, events) {
        state.events = events;
    },
    updateEventsLoading(state, loading) {
        state.events_loading = loading;
    },
    updateEventsCount(state, count) {
        state.events_count = count;
    },
    updateEventsErrors(state, errors) {
        state.events_errors = errors;
    },
    updateEventsFilters(state, filters) {
        state.events_filters = filters;
    },
};

const actions = {

    async duplicateInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/customerinvoices/${state.invoice.id}/duplicate/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchBillingAccount({ commit, dispatch, state, rootGetters }, params) {
        if (state.invoice.biller != store.getters['session/current_entity_id']) {
            return;
        }
        if (rootGetters['session/current_user_permissions'].indexOf("billing.view_billingaccount") === -1) {
            return;
        }


        commit('updateBillingAccountLoading', true);
        commit('updateBillingAccountLoadingErrors', null);
        try {
            const response = await axios.get(`/api/billingaccounts/${state.invoice.billingaccount}/`);
            commit('updateBillingAccount', response.data);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateBillingAccountLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateBillingAccountLoading', false);
        }
    },

    async fetchEntities({ commit, dispatch, state }, params) {
        commit('updateEntitiesLoading', true);
        try {
            const response = await axios.get("/api/entities/", { params: { limit: 1000, ordering: 'name', profile: params, parent: store.getters['session/current_entity_id'] } });
            commit('updateEntities', response.data.results);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateEntitiesLoadingErrors', error.details);
            throw error;
        }
    },

    async fetchInvoice({ commit, dispatch, state }, params) {
        commit('updateInvoiceLoading', true);
        commit('updateInvoiceLoadingErrors', null);

        let url;
        if (params && params.invoice_id) {
            url = `/api/customerinvoices/${params.invoice_id}/`;
        } else if (state.invoice) {
            url = `/api/customerinvoices/${state.invoice.id}/`;
        } else {
            // Receipt / orphan invoice-item UIs may call this without a loaded invoice.
            commit('updateInvoiceLoading', false);
            return null;
        }
        try {
            const response = await axios.get(url);
            commit('updateInvoiceLoading', false);
            commit('updateInvoice', response.data);
            dispatch('fetchInvoiceItems');
            dispatch('fetchBillingAccount');
            dispatch('fetchEvents');
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateInvoiceLoading', false);
            commit('updateInvoiceLoadingErrors', error.details);
            throw error;
        }
    },

    async fetchContractLine({ commit, dispatch, state }, params) {

        let url = `/api/contractlines/${params.contractline_id}/`;
        try {
            const response = await axios.get(url);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async replayInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/customerinvoices/${params.invoice.id}/replay/`);
            dispatch('fetchInvoice');
            dispatch('fetchInvoiceItems');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async replayInvoiceItem({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/customerinvoiceitems/${params.invoiceitem.id}/replay/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async deleteInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.delete(`/api/customerinvoices/${params.instance.id}/`);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
        }
    },

    async archiveInvoice({ commit, dispatch, state }, params) {
        const invoiceId = params?.instance?.id || state.invoice?.id;
        try {
            const response = await axios.post(`/api/customerinvoices/${invoiceId}/archive/`);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('fetchInvoice');
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
        }
    },

    async unarchiveInvoice({ commit, dispatch, state }, params) {
        const invoiceId = params?.instance?.id || state.invoice?.id;
        try {
            const response = await axios.post(`/api/customerinvoices/${invoiceId}/unarchive/`);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('fetchInvoice');
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
        }
    },

    async assignInvoice({ commit, dispatch, state }, { invoice, user }) {
        try {
            const response = await axios.patch(`/api/customerinvoices/${invoice.id}/`, {
                assigned_to: user,
            });
            commit('updateInvoice', response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },


    async duplicateInvoiceItem({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/customerinvoiceitems/${params.instance.id}/duplicate/`);
            await dispatch('fetchInvoiceItems');
            await dispatch('fetchInvoice');
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async deleteInvoiceItem({ commit, dispatch, state }, params) {
        try {
            const response = await axios.delete(`/api/customerinvoiceitems/${params.instance.id}/`);
            dispatch('fetchInvoice');
            dispatch('fetchInvoiceItems');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
        }
    },


    async fetchInvoiceItems({ commit, dispatch, state }, params) {
        commit('updateInvoiceItemsLoading', true);
        commit('updateInvoiceItemsLoadingErrors', null);

        if (state.invoiceitems_cancel_source) {
            state.invoiceitems_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateInvoiceItemsCancelSource', cancelSource);

        try {
            const response = await axios.get("/api/customerinvoiceitems/", {
                params: state.invoiceitems_filters,
                cancelToken: state.invoiceitems_cancel_source?.token,
            });
            commit('updateInvoiceItems', response.data.results);
            commit('updateInvoiceItemsCount', response.data.count);
            commit('updateInvoiceItemsCancelSource', null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            let error = utils.handleError(xhr_error);
            commit('updateInvoiceItemsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateInvoiceItemsLoading', false);
        }
    },

    async reorderInvoiceItems({ dispatch, state }, { item_ids }) {
        try {
            await axios.post("/api/customerinvoiceitems/reorder/", {
                invoice: state.invoice.id,
                item_ids,
            });
            await dispatch("fetchInvoiceItems");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async applyInvoiceItemsSort({ commit, dispatch, state }) {
        try {
            const response = await axios.get("/api/customerinvoiceitems/", {
                params: {
                    invoice: state.invoice.id,
                    ordering: state.invoiceitems_filters.ordering,
                    limit: Math.max(state.invoiceitems_count || 0, 1),
                    offset: 0,
                },
            });
            const item_ids = (response.data.results || []).map((item) => item.id);
            await axios.post("/api/customerinvoiceitems/reorder/", {
                invoice: state.invoice.id,
                item_ids,
            });
            commit("updateInvoiceItemsFilters", {
                ...state.invoiceitems_filters,
                ordering: DEFAULT_INVOICEITEMS_ORDERING,
                offset: 0,
            });
            await dispatch("fetchInvoiceItems");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    exportInvoiceItemsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        filters.invoice = state.invoice.id;
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        filters.ordering = DEFAULT_INVOICEITEMS_ORDERING;
        
        const invoiceNumber = state.invoice?.invoice_id || state.invoice?.id || '';
        
        $utils.downloadFile({
            url: "/api/customerinvoiceitems/",
            params: filters,
        }, $utils.getFilename('customerInvoiceItems', { invoice_number: invoiceNumber }));
    },

    async createCreditNote({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/customerinvoices/${state.invoice.id}/credit/`, params);
            dispatch('fetchInvoice');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async markInvoiceAsSent({ commit, dispatch, state }, params) {
        try {
            commit('updateMarkingAsSent', true);
            const response = await axios.post(`/api/customerinvoices/${state.invoice.id}/sent/`);
            await dispatch('fetchInvoice');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateMarkingAsSent', false);
        }
    },

    async markInvoiceAsPaid({ commit, dispatch, state }, { paid = true } = {}) {
        try {
            commit('updateMarkingAsPaid', true);
            const response = await axios.post(
                `/api/customerinvoices/${state.invoice.id}/paid/`,
                { paid }
            );
            await dispatch('fetchInvoice');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateMarkingAsPaid', false);
        }
    },

    async invalidateInvoice({ commit, dispatch, state }) {
        commit('updateInvalidatingInvoice', true);
        try {
            const response = await axios.post(
                `/api/customerinvoices/${state.invoice.id}/invalidate/`
            );
            await dispatch('fetchInvoice');
            await dispatch('fetchEvents');
            await dispatch('session/fetchStats', null, { root: true });
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateInvalidatingInvoice', false);
        }
    },

    async fetchEvents({ commit, dispatch, state }) {
        commit('updateEventsLoading', true);

        try {
            const response = await axios.get('/api/events/', { params: state.events_filters });
            commit('updateEvents', response.data.results);
            commit('updateEventsCount', response.data.count);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateEventsErrors', error.details);
            throw error;
        } finally {
            commit('updateEventsLoading', false);
        }
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/customerinvoiceitems/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            await dispatch("fetchInvoiceItems");
            await dispatch("fetchInvoice");
            dispatch("session/fetchStats", null, { root: true });
            dispatch("billingstats/fetchAllStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
